import React from 'react';

/**
 * HAStatusIndicator - Visual indicator for channel publisher status.
 * - Single dot for 1 publisher (when expectedStreams is 1 or not set)
 * - Two overlapping dots for 2 publishers, or when expectedStreams=2 and memberCount=1 (●○)
 * - Dot colors reflect stream state (active=green, inactive=red outline)
 * - Loading spinner and error (yellow triangle) states
 *
 * @param {Object} props
 * @param {number} [props.memberCount] - Number of active publishers (0, 1, or 2+)
 * @param {number} [props.activeCount] - Number of active streams (for mixed state: 1 active, 1 down)
 * @param {number} [props.expectedStreams] - User-selected expected streams (1 or 2). When 2 and memberCount=1, shows ●○
 * @param {'loading'|'error'|'ready'} [props.state] - loading, error, or ready
 */
const HAStatusIndicator = ({ memberCount = 0, activeCount, expectedStreams, state = 'ready' }) => {
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center" title="Checking publisher status...">
        <div className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex items-center justify-center" title="Status check failed">
        <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3L2 21h20L12 3zm0 3.6L19.5 19.5h-15L12 6.6z" />
        </svg>
      </div>
    );
  }

  const count = memberCount ?? 0;
  const active = activeCount ?? count;

  if (count === 0) {
    return (
      <div className="flex items-center justify-center" title="No active publishers">
        <div className="w-3 h-3 rounded-full border-2 border-red-500" />
      </div>
    );
  }

  // memberCount=1, expectedStreams=2: show ●○ (one present, one expected-but-missing)
  if (count === 1 && expectedStreams === 2) {
    const firstActive = active >= 1;
    const secondActive = false; // second slot is expected but missing
    return (
      <div className="flex items-center justify-center relative" title="1 of 2 expected publishers">
        <div className="relative w-5 h-3">
          <div
            className={`absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${
              firstActive ? 'bg-green-500' : 'border-2 border-red-500'
            }`}
            style={{ zIndex: 2 }}
          />
          <div
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${
              secondActive ? 'bg-green-500' : 'border-2 border-red-500'
            }`}
            style={{ zIndex: 1 }}
          />
        </div>
      </div>
    );
  }

  if (count === 1) {
    const isActive = active >= 1;
    return (
      <div className="flex items-center justify-center" title={isActive ? '1 active publisher' : 'No active publishers'}>
        <div
          className={`w-3 h-3 rounded-full ${
            isActive ? 'bg-green-500' : 'border-2 border-red-500'
          }`}
        />
      </div>
    );
  }

  // 2+ publishers: overlapping dots (Venn-diagram style)
  // First dot: active if activeCount >= 1
  // Second dot: active if activeCount >= 2
  const firstActive = active >= 1;
  const secondActive = active >= 2;

  return (
    <div className="flex items-center justify-center relative" title={`${active} of ${count} active`}>
      <div className="relative w-5 h-3">
        <div
          className={`absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${
            firstActive ? 'bg-green-500' : 'border-2 border-red-500'
          }`}
          style={{ zIndex: 2 }}
        />
        <div
          className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${
            secondActive ? 'bg-green-500' : 'border-2 border-red-500'
          }`}
          style={{ zIndex: 1 }}
        />
      </div>
    </div>
  );
};

export default HAStatusIndicator;
